import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "@/lib/site";

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Item({ item, reversed }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center group"
      data-testid={`portfolio-item-${item.key}`}
    >
      {/* Image */}
      <Reveal
        className={`md:col-span-7 ${reversed ? "md:order-2" : "md:order-1"}`}
      >
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative overflow-hidden aspect-[4/3] md:aspect-[16/10]"
          data-testid={`portfolio-image-link-${item.key}`}
        >
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover grayscale scale-[1.02] transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-105"
          />
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 mix-blend-overlay"
            style={{ background: item.accent }}
          />
        </a>
      </Reveal>

      {/* Text */}
      <div className={`md:col-span-5 ${reversed ? "md:order-1" : "md:order-2"}`}>
        <Reveal delay={0.1}>
          <span
            className="font-mono text-sm tracking-[0.2em]"
            style={{ color: item.accent }}
            data-testid={`portfolio-index-${item.key}`}
          >
            {item.index}
          </span>
        </Reveal>
        <Reveal delay={0.15}>
          <h3 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight mt-4 mb-2">
            {item.name}
          </h3>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--mpg-text-soft)] mb-6">
            {item.category}
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="text-base md:text-lg font-light leading-relaxed text-[var(--mpg-text-soft)] mb-8 max-w-md">
            {item.description}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mpg-text)] group/link"
            data-testid={`portfolio-visit-${item.key}`}
          >
            <span className="link-underline">
              Visit {item.link.replace("https://", "")}
            </span>
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1"
            />
          </a>
        </Reveal>
      </div>
    </div>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      className="py-24 md:py-32 lg:py-48 max-w-[1800px] mx-auto px-8 md:px-16"
      data-testid="portfolio"
    >
      <div className="mb-20 md:mb-32 max-w-3xl">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--mpg-gold)] mb-6">
            The Portfolio
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Two companies today. <span className="italic font-light">One standard</span> of
            building.
          </h2>
        </Reveal>
      </div>

      <div className="flex flex-col gap-24 md:gap-40">
        {PORTFOLIO.map((item, i) => (
          <Item key={item.key} item={item} reversed={i % 2 === 1} />
        ))}
      </div>
    </section>
  );
}
